/* ===== game/boss-restorer-art.ts — 환원기 그림 — 시계 눈 핵 · 저마다 다른 축으로 도는 고리 셋(짐벌) · 시곗바늘 · 위성 둘 · 되감기 때 뒤집히는 화면 ===== */
import { mixin } from '../../engine/core/mixin.js';
import { Sprites } from '../sprites.js';
import { Game } from '../game.js';
import { STRIKE_ART, hzGlow } from './boss-hazard-art.js';
/* ★ 계획 §3: 하늘의 고리 장치 — 고리가 따로 돈다, 되감기 때 화면이 거꾸로. 폭풍의 수호자 고리(몸을 가로로 감싼 납작한 궤도)와 달리
   이것은 짐벌이다 — 고리마다 제 축(기울기)을 두고 그 축을 돌아 핵을 가둔 구처럼 보이고, 뒤 반쪽은 핵 뒤 · 앞 반쪽은 핵 앞에 그린다.
   되감기(rewind 기술 · 원점 회귀)는 `flip` 위험 지대를 놓고, 그동안 화면이 위아래로 뒤집혔다 돌아온다(render 의 flipBegin/flipEnd) · 고리와 바늘이 거꾸로 빨리 돈다.
   설계 좌표는 tools/mkbossrig.py restorer_*(핵 48×48 · 고리 64×64 의 반지름 30 · 26 · 22). 사연: docs/code-history.md#h241 */

type C2 = CanvasRenderingContext2D;
const K = 4, CW = 48 * K, PW = 64 * K, GLOW = '#bcd8f4';
const rh = (s: number, i: number) => { const v = Math.sin(s * 91.7 + i * 47.3) * 43758.5453; return v - Math.floor(v); };
const NOISE: HTMLCanvasElement[] = [];
/** 잡음 판 128² 넷(한 번만 굽는다) */
function noiseTile(i: number) {
  if (!NOISE[i]) {
    const cv = document.createElement('canvas'); cv.width = cv.height = 128; const x = cv.getContext('2d')!, d = x.createImageData(128, 128);
    for (let k = 0; k < d.data.length; k += 4) { const v = Math.random() * 255; d.data[k] = d.data[k + 1] = d.data[k + 2] = v; d.data[k + 3] = 255; }
    x.putImageData(d, 0, 0); NOISE[i] = cv;
  }
  return NOISE[i];
}
/** 고리 셋 — [축 기울기, 축 돌기 빠르기, 제 돌기 빠르기, 배율] */
const RINGS: [number, number, number, number][] = [[0.35, 0.55, 0.25, 1.6], [-0.9, -0.8, -0.4, 1.6], [1.4, 1.1, 0.6, 1.6]];

export const BossRestorerArt: Bag = {
  /** 고리 반쪽 — back 이면 핵 뒤 반쪽 */
  drawRestorerRing(c: C2, pim: HTMLImageElement, i: number, X: number, Y: number, tilt: number, th: number, spin: number, k: number, back: boolean) {
    const front = Math.sin(th) > 0 ? -1 : 1;                                   // 앞으로 온 반쪽(위 · 아래)
    const half = back ? -front : front;
    c.save(); c.translate(X, Y); c.rotate(tilt);
    c.beginPath(); c.rect(-PW * k, half < 0 ? -PW * k : 0, PW * 2 * k, PW * k); c.clip();
    c.scale(k, k * Math.max(0.04, Math.abs(Math.cos(th)))); c.rotate(spin);
    c.drawImage(pim, i * PW, 0, PW, PW, -PW / 2, -PW / 2, PW, PW);
    c.restore();
  },
  /** 몸(actors 단계 · drawEnemy 가 넘긴다) */
  drawRestorer(c: C2, e: Bag, sx: number, sy: number) {
    const im = Sprites.want('restorer'), pim = Sprites.want('restorer_parts'); if (!im || !pim) return false;
    const T = this.time || 0, st = Math.min(2, Math.round((e.pf || 0) * 2));
    const rew = (this.bhz || []).some((h: Bag) => h.k === 'flip' && h.t < h.life);
    const A = e.rsA || (e.rsA = { t0: T, th: [0, 1.2, 2.4], sp: [0, 0, 0], hand: 0 });
    const dt = this.paused ? 0 : Math.max(0, Math.min(0.1, T - A.t0)); A.t0 = T;
    const rate = rew ? -6 : 1 + (e.pf || 0) * 0.6;                            // 되감기 — 거꾸로 빨리
    RINGS.forEach(([, w, s], i) => { A.th[i] += w * rate * dt; A.sp[i] += s * rate * dt; });
    A.hand += rate * dt;
    const X = sx + e.w / 2, Y = sy + e.h / 2 + Math.sin(T * 0.9) * 8;
    c.save(); c.imageSmoothingEnabled = false;
    /* 위성 둘 — 바깥 고리를 따라 돈다(깊이로 앞뒤를 가른다) */
    const sats = [0, Math.PI].map(o => {
      const [tilt, , , k] = RINGS[0], a = A.sp[0] * 2 + o, R = 29 * K * k, th = A.th[0];
      const lx = Math.cos(a) * R, ly = Math.sin(a) * R * Math.cos(th), z = Math.sin(a) * Math.sin(th);
      return { x: X + lx * Math.cos(tilt) - ly * Math.sin(tilt), y: Y + lx * Math.sin(tilt) + ly * Math.cos(tilt), z };
    });
    const sat = (q: Bag) => c.drawImage(pim, 5 * PW, 0, PW, PW, q.x - PW * 0.5, q.y - PW * 0.5, PW, PW);
    sats.filter(q => q.z > 0).forEach(sat);
    RINGS.forEach(([tilt, , , k], i) => this.drawRestorerRing(c, pim, i, X, Y, tilt + Math.sin(T * 0.2 + i) * 0.1, A.th[i], A.sp[i], k, true));
    /* 핵 — 시계 눈 · 바늘 */
    const fr = st * 2 + ((T % 1.6) < 0.8 ? 0 : 1), ck = 1.3;
    c.save(); c.translate(X, Y); c.scale(ck, ck);
    c.drawImage(im, fr * CW, 0, CW, CW, -CW / 2, -CW / 2, CW, CW);
    if (e.flash > 0) { c.globalCompositeOperation = 'lighter'; c.globalAlpha = Math.min(0.5, e.flash * 4); c.drawImage(im, fr * CW, 0, CW, CW, -CW / 2, -CW / 2, CW, CW); c.globalCompositeOperation = 'source-over'; c.globalAlpha = 1; }
    for (const [fi, sp] of [[4, 0.12], [3, 1.0]] as [number, number][]) {
      c.save(); c.rotate(A.hand * sp); c.scale(0.55, 0.55); c.drawImage(pim, fi * PW, 0, PW, PW, -PW / 2, -PW / 2, PW, PW); c.restore();
    }
    c.restore();
    RINGS.forEach(([tilt, , , k], i) => this.drawRestorerRing(c, pim, i, X, Y, tilt + Math.sin(T * 0.2 + i) * 0.1, A.th[i], A.sp[i], k, false));
    sats.filter(q => q.z <= 0).forEach(sat);
    c.restore();
    e.rsEye = [X + this.cam.x, Y + this.cam.y];
    return true;
  },
  /** 빛(fx 단계) — 눈 · 되감기 땐 더 밝게 */
  drawRestorerGlow(f: any) {
    const c: C2 = f.c, T = this.time || 0;
    for (const e of this.ents) {
      if (!e || e.dead || !e.def || e.def.ai !== 'b_restorer' || !e.rsEye) continue;
      const rew = (this.bhz || []).some((h: Bag) => h.k === 'flip' && h.t < h.life);
      c.save(); c.globalCompositeOperation = 'lighter';
      hzGlow(c, e.rsEye[0] - f.camX, e.rsEye[1] - f.camY, (rew ? 70 : 34) + Math.sin(T * 3) * 4, GLOW, rew ? 0.8 : 0.5);
      c.restore();
    }
  },
  /** 되감기 — 화면이 위아래로 뒤집혔다 돌아온다(sky 단계 맨 앞에서 건다 · screen 단계 맨 앞에서 푼다) */
  flipBegin(f: any) {
    const h = (this.bhz || []).find((q: Bag) => q.k === 'flip' && q.t < q.life); if (!h || this.fxScale() < 0.3) return;   // 화면 효과를 줄였으면 뒤집지 않는다(어지럼)
    const E = 0.35, v = h.t < E ? Math.cos(Math.PI * h.t / E) : h.t > h.life - E ? Math.cos(Math.PI * (h.life - h.t) / E) : -1;
    const c: C2 = f.c;
    c.fillStyle = '#05060a'; c.fillRect(0, 0, this.W, this.H);
    c.save(); c.translate(0, this.H / 2); c.scale(1, Math.abs(v) < 0.03 ? 0.03 * Math.sign(v || 1) : v); c.translate(0, -this.H / 2);
    f.flipped = 1;
  },
  flipEnd(f: any) {
    if (!f.flipped) return;
    const c: C2 = f.c, T = this.time || 0; c.restore(); f.flipped = 0;
    /* 되감기 잡음 — 테이프를 거꾸로 감는 화면: 모래 잡음 · 가로로 찢겨 밀린 띠 · 색 어긋남 · 밑단 머리 잡음.
       ★ 가로 줄만 긋지 말 것("되감기를 선이 아니라 진짜 잡음으로" 점검판 2026-11-03). 사연: docs/code-history.md#h262 */
    const fr = Math.floor(T * 24), cv = c.canvas, m = c.getTransform(), sxk = m.a || 1, syk = m.d || 1;
    c.save(); c.setTransform(1, 0, 0, 1, 0, 0);
    for (let i = 0; i < 4; i++) {                                              // 찢긴 띠 — 그 줄의 화면을 옆으로 밀어 다시 찍는다
      const y = Math.floor(rh(fr, i) * cv.height), hh = Math.max(4, Math.floor((4 + rh(fr, i + 9) * 26) * syk)), dx = Math.round((rh(fr, i + 3) - 0.5) * 70 * sxk);
      c.drawImage(cv, 0, y, cv.width, hh, dx, y, cv.width, hh);
      c.globalCompositeOperation = 'lighter'; c.globalAlpha = 0.25;
      c.fillStyle = i % 2 ? '#ff4060' : '#40e0ff'; c.fillRect(dx + (i % 2 ? 4 : -4) * sxk, y, cv.width, hh);
      c.globalCompositeOperation = 'source-over'; c.globalAlpha = 1;
    }
    const N = noiseTile(fr % 4);                                              // 모래 잡음
    c.globalAlpha = 0.22; c.fillStyle = c.createPattern(N, 'repeat')!;
    c.translate(-Math.floor(rh(fr, 20) * 128), -Math.floor(rh(fr, 21) * 128)); c.fillRect(0, 0, cv.width + 128, cv.height + 128); c.setTransform(1, 0, 0, 1, 0, 0);
    c.globalAlpha = 1;
    const by = cv.height - Math.floor((18 + rh(fr, 30) * 14) * syk);           // 밑단 머리 잡음 — 일그러진 띠
    c.fillStyle = 'rgba(10,12,20,.6)'; c.fillRect(0, by, cv.width, cv.height - by);
    c.fillStyle = 'rgba(255,255,255,.7)';
    for (let i = 0; i < 40; i++) c.fillRect(rh(fr, i + 40) * cv.width, by + rh(fr, i + 90) * (cv.height - by), (6 + rh(fr, i + 140) * 40) * sxk, Math.max(1, syk));
    for (let i = 0; i < 14; i++) c.fillRect(rh(fr, i + 200) * cv.width, rh(fr, i + 220) * cv.height, (10 + rh(fr, i + 240) * 80) * sxk, Math.max(1, syk));   // 흰 줄 튐
    c.globalAlpha = 0.12; c.fillStyle = GLOW; c.fillRect(0, 0, cv.width, cv.height);
    c.restore();
  },
  /** 과거의 잔상 — 분신이 진짜 몸 그대로(고리 · 위성까지)인데 빛바랜 옛 필름처럼: 뒤로 끌리는 겹 · 찢겨 밀린 줄 · 깜빡임 */
  drawRestorerGhost(c: C2, h: Bag, x: number, y: number, f: number) {
    const T = this.time || 0, S = 640;
    const oc = this._rsGhost || (this._rsGhost = document.createElement('canvas')); oc.width = S; oc.height = S;
    const o = oc.getContext('2d')!, w = h.r, fake: Bag = { w, h: h.r, pf: 0, flash: 0, rsA: h.rsA || (h.rsA = null) };
    if (!this.drawRestorer(o, fake, S / 2 - w / 2, S / 2 - h.r / 2)) return;
    h.rsA = fake.rsA;
    const fr = Math.floor(T * 16) + (h.sd || 0) * 7;
    c.save();
    c.globalAlpha = f * 0.22;                                                  // 지나온 자리 — 뒤로 끌리는 겹 둘
    for (const k of [2, 1]) c.drawImage(oc, x - S / 2 - k * 14 * (h.flip ? -1 : 1), y - S / 2 + k * 6, S, S);
    try { c.filter = 'sepia(.85) saturate(.7) brightness(1.15)'; } catch (e) { /* 필터가 없는 브라우저는 그대로 */ }
    const band = 16;
    for (let yy = 0; yy < S; yy += band) {                                     // 줄마다 조금씩 밀린다 — 몇 줄은 크게
      const r = rh(fr, yy), dx = r < 0.12 ? (rh(fr, yy + 1) - 0.5) * 40 : (r - 0.5) * 3;
      c.globalAlpha = f * (0.6 + 0.25 * Math.sin(T * 9 + yy)) * (rh(fr, yy + 2) < 0.06 ? 0.2 : 1);
      c.drawImage(oc, 0, yy, S, band, x - S / 2 + dx, y - S / 2 + yy, S, band);
    }
    c.filter = 'none'; c.globalCompositeOperation = 'lighter'; c.globalAlpha = f * 0.18;   // 색 어긋남
    c.drawImage(oc, x - S / 2 + 3, y - S / 2, S, S);
    c.restore();
  },
};
mixin(Game.prototype, BossRestorerArt, true);

/** 빛바늘 — 하늘에서 내리꽂는 바늘. 예고 땐 맞을 폭의 두 날이 또렷한 빛줄로 서고(경계) 바늘이 위에서 겨눈다 · 맞으면 바늘이 박히며 흰 기둥과 사방 빛살 */
function needle(c: C2, x: number, tipY: number, len: number, w: number) {
  const g = c.createLinearGradient(x - w, 0, x + w, 0); g.addColorStop(0, '#8a6a2a'); g.addColorStop(0.45, '#fff4d0'); g.addColorStop(0.6, '#e8c870'); g.addColorStop(1, '#6a4e1e');
  c.beginPath(); c.moveTo(x, tipY); c.lineTo(x + w, tipY - len * 0.2); c.lineTo(x + w * 0.6, tipY - len); c.lineTo(x - w * 0.6, tipY - len); c.lineTo(x - w, tipY - len * 0.2); c.closePath();
  c.fillStyle = g; c.fill(); c.strokeStyle = '#2a2010'; c.lineWidth = 1.2; c.stroke();
  c.beginPath(); c.arc(x, tipY - len - w * 0.8, w * 0.9, 0, Math.PI * 2); c.strokeStyle = '#e8c870'; c.lineWidth = 2; c.stroke();   // 바늘 귀
}
Object.assign(STRIKE_ART, {
  needle: {
    tell(c: C2, h: Bag, x: number, y: number, k: number, T: number) {
      const ty = Math.max(y - h.hh, h.top ?? y - h.hh), hw = h.w / 2, beat = k > 0.7 ? 0.5 + 0.5 * Math.sin(T * 40) : 0;
      c.save(); c.globalCompositeOperation = 'lighter';
      const g = c.createLinearGradient(0, ty, 0, y); g.addColorStop(0, 'rgba(255,236,180,0)'); g.addColorStop(1, `rgba(255,236,180,${0.1 + k * 0.2})`);
      c.fillStyle = g; c.fillRect(x - hw, ty, h.w, y - ty);
      c.fillStyle = `rgba(255,248,220,${0.35 + k * 0.5 + beat * 0.15})`;   // 두 날 — 피할 폭이 또렷하게
      c.fillRect(x - hw - 1, ty, 2, y - ty); c.fillRect(x + hw - 1, ty, 2, y - ty);
      c.restore();
      c.strokeStyle = `rgba(255,240,200,${0.5 + k * 0.5})`; c.lineWidth = 2;   // 바닥 꺾쇠
      c.beginPath(); c.moveTo(x - hw, y - 8); c.lineTo(x - hw, y); c.lineTo(x - hw + 8, y); c.moveTo(x + hw, y - 8); c.lineTo(x + hw, y); c.lineTo(x + hw - 8, y); c.stroke();
      needle(c, x + (k > 0.7 ? Math.sin(T * 60) : 0), ty + 30 + k * 30, 46, 4);
      hzGlow(c, x, ty + 30 + k * 30, 10 + k * 8, '#fff0c0', 0.6 + k * 0.3);
    },
    hit(c: C2, h: Bag, x: number, y: number, f: number, T: number) {
      const ty = Math.max(y - h.hh, h.top ?? y - h.hh), a = f < 0.15 ? 1 : 1 - (f - 0.15) / 0.85, fall = Math.min(1, f / 0.08);
      c.save(); c.globalCompositeOperation = 'lighter';
      const w = h.w * (0.9 + Math.sin(T * 40) * 0.05), g = c.createLinearGradient(x - w / 2, 0, x + w / 2, 0);
      g.addColorStop(0, 'rgba(255,200,90,0)'); g.addColorStop(0.25, `rgba(255,220,130,${0.7 * a})`); g.addColorStop(0.5, `rgba(255,255,240,${a})`); g.addColorStop(0.75, `rgba(255,220,130,${0.7 * a})`); g.addColorStop(1, 'rgba(255,200,90,0)');
      c.fillStyle = g; c.fillRect(x - w / 2, ty, w, (y - ty) * fall);
      c.fillStyle = `rgba(255,252,235,${a})`; c.fillRect(x - w / 2 - 1, ty, 2, y - ty); c.fillRect(x + w / 2 - 1, ty, 2, y - ty);   // 경계 날
      for (let i = 0; i < 12; i++) {                                         // 박힌 자리 빛살
        const an = -Math.PI * (i / 11), L = (30 + rh(h.sd || 1, i) * 50) * (0.4 + f);
        c.strokeStyle = `rgba(255,240,190,${0.8 * a})`; c.lineWidth = 2; c.beginPath(); c.moveTo(x, y - 2); c.lineTo(x + Math.cos(an) * L, y - 2 + Math.sin(an) * L * 0.5); c.stroke();
      }
      c.restore();
      hzGlow(c, x, y - 4, w * 1.4, '#ffe8a0', 0.9 * a);
      c.globalAlpha = Math.min(1, a * 1.5); needle(c, x, y + 6, 50, 4.5); c.globalAlpha = 1;   // 바닥에 박힌 바늘
    }
  }
});
